import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { Close as CloseIcon, ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  Box, 
  Typography, 
  Button, 
  Stack, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  MenuItem,
  TextField
} from '@mui/material';
import { useSnackbar } from 'notistack';
import * as carApi from '../../lib/api/car'; 
import BreadcrumbsContainer from '../../containers/common/breadcurmbs/BreadcrumbsContainer';

dayjs.extend(isBetween);

const CarReservListContainer = ({ loginUser }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isMounted = useRef(true);
  
  const [isLoading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [carAssets, setCarAssets] = useState([]);
  const [selectedCarUuid, setSelectedCarUuid] = useState('');
  const [currentDate, setCurrentDate] = useState(dayjs());

  const colorPalette = useMemo(() => [
    '#0052cc', '#138654', '#7952b3', '#e67e22', '#d9381e', 
    '#2d9cdb', '#109618', '#990099', '#3b5998', '#009688'
  ], []);

  const filteredEvents = useMemo(() => {
    if (!selectedCarUuid) return events;
    return events.filter(event => event.carUuid === selectedCarUuid);
  }, [events, selectedCarUuid]);

  const formik = useFormik({
    initialValues: {
      centerId: '',
      carType: '',
      searchKeyword: '',
      searchStrtDt: currentDate.toDate(),
      searchEndDt: new Date()
    },
    onSubmit: () => {}
  });

  useEffect(() => {
    formik.setFieldValue('searchStrtDt', currentDate.startOf('month').toDate());
  }, [currentDate, formik.setFieldValue]);
  useEffect(() => {
    let isAssetMounted = true;
    const fetchAssets = async () => {
        try {
            const response = await carApi.getCarAssetList();
            if (isAssetMounted) {
                setCarAssets(response.data.list || []);
            }
        } catch (error) {
            console.error("차량 목록 호출 실패:", error);
        }
    };
    fetchAssets();
    return () => { isAssetMounted = false; };
  }, []);

  const fetchListData = useCallback(async () => {
    try {
      if (isMounted.current) setLoading(true);
      
      const baseDate = formik.values.searchStrtDt ? dayjs(formik.values.searchStrtDt) : dayjs();
      const expandedStart = baseDate.subtract(1, 'month').startOf('month').format('YYYY-MM-DD 00:00');
      const expandedEnd = baseDate.add(1, 'month').endOf('month').format('YYYY-MM-DD 23:59');

      const params = {
        searchKeyword: formik.values.searchKeyword || '',
        startDate: expandedStart,
        endDate: expandedEnd,
        ...(localStorage.getItem('loginMode') === 'car_service' && { usrUuid: loginUser?.usrUuid })
      };
      
      const response = await carApi.getCarReservPcList(params);
      const serverList = response.data?.list || [];

      const mappedEvents = serverList.map((item) => {
        const getCarColor = (carUuid) => {
          if (!carUuid) return '#e67e22';
          const assetIndex = carAssets.findIndex(car => car.carUuid === carUuid);
          if (assetIndex !== -1) {
            return colorPalette[assetIndex % colorPalette.length];
          }
          return '#e67e22';
        };

        const eventColor = getCarColor(item.carUuid);

        return {
          id: item.reservId || item.carUuid || item.logId,
          carUuid: item.carUuid,
          title: `[${item.dptNm || '부서'}] ${item.carNumber || '차량'} - ${item.usrNm || '운전자'}`,
          start: item.startDate ? dayjs(item.startDate).format('YYYY-MM-DD') : '',
          end: item.endDate ? dayjs(item.endDate).format('YYYY-MM-DD') : '',
          rawStart: item.startDate,
          rawEnd: item.endDate,
          color: eventColor,
          carNo: item.carNumber || '번호 정보 없음',
          carModel: item.carModel || '모델 정보 없음',
          driver: item.usrNm || '미지정',
          deptName: item.dptNm || '부서 미지정',
          purpose: item.visitPartnerNm ? `${item.visitPartnerNm} 방문` : '용도 미기재',
          status: item.statusYn === 'E' ? '이용 완료' : '예약 및 운행 중',
          parking: item.parking || '정보 없음'
        };
      });

      if (isMounted.current) setEvents(mappedEvents);
    } catch (err) {
      if (isMounted.current) enqueueSnackbar('데이터 로드 실패', { variant: 'error' });
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [formik.values.searchStrtDt, formik.values.searchKeyword, enqueueSnackbar, loginUser?.usrUuid, carAssets, colorPalette]);

  useEffect(() => {
    isMounted.current = true;
    fetchListData();
    return () => { isMounted.current = false; };
  }, [fetchListData]);

  const calendarWeeks = useMemo(() => {
    const startOfMonth = currentDate.startOf('month');
    const endOfMonth = currentDate.endOf('month');
    const startCalendarDate = startOfMonth.startOf('week');
    const endCalendarDate = endOfMonth.endOf('week');

    const totalDays = endCalendarDate.diff(startCalendarDate, 'day') + 1;
    const dayList = [];

    for (let i = 0; i < totalDays; i++) {
      dayList.push(startCalendarDate.add(i, 'day'));
    }

    const weeks = [];
    for (let i = 0; i < dayList.length; i += 7) {
      weeks.push(dayList.slice(i, i + 7));
    }
    return weeks;
  }, [currentDate]);

  const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

  const handleEventClick = useCallback((eventData) => {
    setSelectedEvent(eventData);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  }, []);

  const handleCarChange = useCallback((e) => {
    setSelectedCarUuid(e.target.value);
  }, []);

  const handlePrevMonth = () => setCurrentDate(prev => prev.subtract(1, 'month'));
  const handleNextMonth = () => setCurrentDate(prev => prev.add(1, 'month'));
  const handleToday = () => setCurrentDate(dayjs());
  return (
    <Box sx={{ width: '100%', p: 0, height: 'auto' }}>
      <Stack gap={1} sx={{ width: '100%', mb: 2 }}>
        <BreadcrumbsContainer title="총무 관리 / 차량 예약 기록 / 리스트" icon="common" />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mt: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={handlePrevMonth} variant="outlined" size="small" sx={{ border: '1px solid #ced4da', borderRadius: '4px' }}>
              <ChevronLeft size="small" />
            </IconButton>
            <Typography variant="h6" fontWeight="bold" sx={{ minWidth: '120px', textAlign: 'center', color: '#333333' }}>
              {currentDate.format('YYYY년 MM월')}
            </Typography>
            <IconButton onClick={handleNextMonth} variant="outlined" size="small" sx={{ border: '1px solid #ced4da', borderRadius: '4px' }}>
              <ChevronRight size="small" />
            </IconButton>
            <Button onClick={handleToday} variant="outlined" size="small" sx={{ borderColor: '#ced4da', color: '#495057', textTransform: 'none' }}>
              오늘
            </Button>
          </Stack>

          <TextField
            variant="standard"
            select
            name="carUuid"
            label="배차 차량"
            value={selectedCarUuid}
            onChange={handleCarChange}
            sx={{ width: '180px' }}
          >
            <MenuItem value="">전체</MenuItem>
            {carAssets.map((car) => (
              <MenuItem key={car.carUuid} value={car.carUuid}>
                {car.carNumber} ({car.carModel})
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Stack>

      <Box 
        className="carmng-calendar-card" 
        sx={{ 
          p: '24px', 
          backgroundColor: '#ffffff', 
          border: '1px solid #e0e0e0', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)', 
          mt: '20px',
          mb: 5,
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          height: 'fit-content'
        }}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #e0e0e0', pb: 1, backgroundColor: '#f1f3f5' }}>
          {weekDays.map((day, idx) => (
            <Typography 
              key={idx} 
              variant="subtitle1" 
              fontWeight="bold"
              sx={{ 
                textAlign: 'center', 
                py: '10px',
                color: idx === 0 ? '#dc3545' : idx === 6 ? '#0d6efd' : '#333333' 
              }}
            >
              {day}
            </Typography>
          ))}
        </Box>

        <Box sx={{ border: '1px solid #e0e0e0', backgroundColor: '#e0e0e0', display: 'flex', flexDirection: 'column', gap: '1px' }}>
          {calendarWeeks.map((week, weekIdx) => {
            const weekStart = week[0].startOf('day');
            const weekEnd = week[6].endOf('day');

            const matchedEvents = filteredEvents
              .filter(event => {
                if (!event.rawStart || !event.rawEnd) return false;
                const eStart = dayjs(event.rawStart);
                const eEnd = dayjs(event.rawEnd);
                return eStart.isBefore(weekEnd) && eEnd.isAfter(weekStart);
              })
              .sort((a, b) => {
                const aStart = dayjs(a.rawStart);
                const bStart = dayjs(b.rawStart);
                if (!aStart.isSame(bStart)) return aStart.diff(bStart);
                const aEnd = dayjs(a.rawEnd);
                const bEnd = dayjs(b.rawEnd);
                return bEnd.diff(aEnd);
              });

            const timelineTracks = [];
            const renderedWeekEvents = [];

            matchedEvents.forEach(event => {
              const eStart = dayjs(event.rawStart);
              const eEnd = dayjs(event.rawEnd);

              const displayStart = eStart.isBefore(weekStart) ? weekStart : eStart;
              const displayEnd = eEnd.isAfter(weekEnd) ? weekEnd : eEnd;

              const leftHours = displayStart.diff(weekStart, 'hour');
              const spanHours = displayEnd.diff(displayStart, 'hour');
              const rightHours = leftHours + spanHours;

              let targetTrackIndex = -1;
              for (let i = 0; i < timelineTracks.length; i++) {
                const hasOverlap = timelineTracks[i].some(allocated => {
                  return !(rightHours <= allocated.leftHours || leftHours >= allocated.rightHours);
                });
                if (!hasOverlap) {
                  targetTrackIndex = i;
                  break;
                }
              }

              if (targetTrackIndex === -1) {
                timelineTracks.push([{ leftHours, rightHours }]);
                targetTrackIndex = timelineTracks.length - 1;
              } else {
                timelineTracks[targetTrackIndex].push({ leftHours, rightHours });
              }

              renderedWeekEvents.push({
                ...event,
                leftHours,
                spanHours,
                topLevel: targetTrackIndex
              });
            });

            const maxEventCount = timelineTracks.length;
            const containerHeight = 40 + (maxEventCount * 28) + 10;

            return (
              <Box 
                key={weekIdx} 
                sx={{ 
                  position: 'relative', 
                  backgroundColor: '#ffffff', 
                  minHeight: `${Math.max(130, containerHeight)}px`,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: '1px'
                }}
              >
                {week.map((dayDate, dayIdx) => {
                  const isCurrentMonth = dayDate.month() === currentDate.month();
                  const isToday = dayjs().isSame(dayDate, 'day');

                  return (
                    <Box 
                      key={dayIdx} 
                      sx={{ 
                        backgroundColor: isCurrentMonth ? (isToday ? 'rgba(25, 118, 210, 0.04)' : '#ffffff') : '#fafafa', 
                        p: 1, 
                        display: 'flex', 
                        flexDirection: 'column',
                        height: '100%',
                        boxSizing: 'border-box',
                        borderRight: dayIdx < 6 ? '1px solid #e0e0e0' : 'none'
                      }}
                    >
                      <Typography 
                        variant="body1" 
                        fontWeight="bold" 
                        sx={{ 
                          mb: 1,
                          opacity: isCurrentMonth ? 1 : 0.4,
                          color: dayDate.day() === 0 ? '#dc3545' : dayDate.day() === 6 ? '#0d6efd' : '#333333'
                        }}
                      >
                        {dayDate.date()}
                      </Typography>
                    </Box>
                  );
                })}

                <Box sx={{ position: 'absolute', top: '40px', left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
                  {renderedWeekEvents.map((event) => {
                    const totalWeekHours = 168; 
                    const leftPos = (event.leftHours / totalWeekHours) * 100;
                    const widthPos = (event.spanHours / totalWeekHours) * 100;

                    return (
                      <Box
                        key={event.id}
                        onClick={() => handleEventClick(event)}
                        sx={{
                          position: 'absolute',
                          left: `${leftPos}%`,
                          width: `calc(${widthPos}% - 6px)`,
                          minWidth: '60px',
                          top: `${event.topLevel * 28}px`,
                          height: '24px',
                          backgroundColor: event.color || '#e67e22',
                          color: '#ffffff',
                          px: '8px',
                          mx: '3px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          lineHeight: '24px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          pointerEvents: 'auto',
                          zIndex: 2,
                          boxSizing: 'border-box',
                          '&:hover': { 
                            opacity: 1,
                            zIndex: 10,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                          }
                        }}
                        title={event.title}
                      >
                        {event.title}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Dialog 
        open={isModalOpen}
        onClose={handleCloseModal}
        PaperProps={{ 
          sx: { 
            backgroundColor: '#ffffff', 
            color: '#333333', 
            minWidth: '420px', 
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            borderRadius: '8px'
          } 
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" component="span" fontWeight="bold" color="#111111">예약 상세 정보</Typography>
          <IconButton onClick={handleCloseModal} sx={{ color: '#666666' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers sx={{ borderColor: '#e0e0e0', p: 3 }}>
          {selectedEvent && (() => {
            const props = selectedEvent.extendedProps || selectedEvent;
            const status = props.status || '대기중';

            return (
              <Stack gap={2.5}>
                <Box>
                  <Typography variant="body2" color="#888888" fontWeight="500">부서 / 운전자</Typography>
                  <Typography variant="body1" fontWeight="bold" color="#222222" sx={{ mt: 0.5 }}>
                    {props.deptName || '부서 미지정'} / {props.driver || '미지정'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="#888888" fontWeight="500">차량 모델 / 차량 번호</Typography>
                  <Typography variant="body1" color="#222222" sx={{ mt: 0.5 }} fontWeight="500">
                    {props.carModel || '모델 정보 없음'} ({props.carNo || '번호 정보 없음'})
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="#888888" fontWeight="500">예약 기간</Typography>
                  <Typography variant="body1" color="#222222" sx={{ mt: 0.5 }} fontWeight="500">
                    {props.rawStart ? dayjs(props.rawStart).format('YYYY-MM-DD HH:mm') : '-'} ~ {props.rawEnd ? dayjs(props.rawEnd).format('YYYY-MM-DD HH:mm') : '-'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="#888888" fontWeight="500">방문처 / 용도</Typography>
                  <Typography variant="body1" color="#222222" sx={{ mt: 0.5 }}>
                    {props.purpose || '용도 미기재'}
                  </Typography>
                </Box>
                {/* 
                <Box>
                  <Typography variant="body2" color="#888888" fontWeight="500">주차 위치()</Typography>
                  <Typography variant="body1" color="#222222" sx={{ mt: 0.5 }}>
                    {props.parking || '정보 없음'}
                  </Typography>
                </Box>
                */} 
                <Box>
                  <Typography variant="body2" color="#888888" fontWeight="500" sx={{ mb: 0.5 }}>예약 및 운행 상태</Typography>
                  <Chip 
                    label={status} 
                    size="small"
                    color={status === '이용 완료' ? 'primary' : 'warning'}
                    sx={{ fontWeight: 'bold', borderRadius: '4px' }}
                  />
                </Box>
              </Stack>
            );
          })()}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', backgroundColor: '#fdfdfd' }}>
          <Button 
            onClick={handleCloseModal} 
            variant="outlined" 
            sx={{ borderColor: '#cccccc', color: '#555555', '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#bbbbbb' } }}
          >
            닫기
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CarReservListContainer;
